/**
 * SH Update: Shopper Form JavaScript (Hybrid Version)
 * Created: 2026-01-05
 * Updated: 2026-01-08
 * Purpose: Handles both inline cart form and modal popup with newsletter subscription
 *
 * Architecture:
 *   Cart Page & Other Pages (unified single-view modal):
 *     - Question dropdown always visible at top
 *     - Email section revealed based on selection:
 *       - Personal → Email section with newsletter signup + 30% discount
 *       - Company → Email section with dual buttons (Register / Continue Shopping)
 *       - Account → Redirect to login
 *     - Form is ALWAYS required to checkout (even if modal was dismissed)
 *
 * Session Management:
 *   Uses a session marker to detect new browser/tab sessions.
 *   When a new session is detected (browser closed or new tab), all stored
 *   shopper data is cleared and the form experience starts fresh.
 *
 * Storage:
 *   sessionStorage (per-session, clears when tab closes):
 *     - shopper_session_id: unique session marker
 *     - shopper_type_answered: "true" when form completed this session
 *     - shopper_modal_dismissed: "true" when modal closed without completing
 *
 *   localStorage (cleared on new session):
 *     - shopper_session_id: matches sessionStorage to detect new sessions
 *     - shopper_type: "personal" or "company"
 *     - shopper_email: email address (personal only)
 *     - shopper_company_email: company email address
 *     - shopper_type_timestamp: ISO timestamp
 *
 * Related files:
 *   - snippets/sh-shopper-cart-form.liquid (inline form)
 *   - sections/sh-shopper-modal.liquid (modal in footer)
 *   - assets/sh-shopper.css
 */

(function() {
    'use strict';

    // SH Update: Storage keys
    // Session marker uses both storages to detect new sessions
    // 'answered' and 'dismissed' use sessionStorage (per-session)
    // Other data uses localStorage but is cleared on new session
    var STORAGE_KEYS = {
        sessionId: 'shopper_session_id',      // both storages - session marker
        answered: 'shopper_type_answered',    // sessionStorage - form completed
        dismissed: 'shopper_modal_dismissed', // sessionStorage - modal closed without filling
        type: 'shopper_type',                 // localStorage (cleared on new session)
        email: 'shopper_email',               // localStorage (cleared on new session)
        companyEmail: 'shopper_company_email', // localStorage (cleared on new session)
        timestamp: 'shopper_type_timestamp',  // localStorage (cleared on new session)
        pendingRedirect: 'shopper_pending_redirect', // sessionStorage - redirect URL after form submit
        pendingSuccess: 'shopper_pending_success' // sessionStorage - show success after page reload
    };

    // SH Update: Company redirect URL (can be overridden by data attribute)
    var COMPANY_REDIRECT_URL = '/pages/register-your-company';

    var MOBILE_QUERY = '(max-width: 1023.98px)';

    // SH Update: Selectors
    var SELECTORS = {
        // Cart page inline form
        cartForm: '.js-sh-shopper-cart-form',
        cartSelect: '.js-sh-shopper-select',
        cartError: '.js-sh-shopper-error',

        // Modal (footer)
        modal: '.js-sh-shopper-modal',
        modalOverlay: '.js-sh-shopper-overlay',
        modalClose: '.js-sh-shopper-close',
        modalSelect: '.js-sh-shopper-modal-select',
        questionField: '.js-sh-shopper-question',
        step1Error: '.js-sh-shopper-step1-error',

        // Personal section
        personalSection: '.js-sh-shopper-personal-section',
        personalSuccess: '.js-sh-shopper-personal-success',
        closeSuccessBtn: '.js-sh-close-success',
        emailForm: '.js-sh-shopper-form',
        emailInput: '.js-sh-shopper-email',
        submitBtn: '.js-sh-shopper-submit',
        emailError: '.js-sh-shopper-email-error',

        // Company section
        companySection: '.js-sh-shopper-company-section',
        companyForm: '.js-sh-shopper-company-form',
        companyEmail: '.js-sh-shopper-company-email',
        companyEmailError: '.js-sh-shopper-company-email-error',
        companyButtons: '.js-sh-shopper-company-buttons',
        registerBtn: '.js-sh-shopper-register-btn',
        continueBtn: '.js-sh-shopper-continue-btn',

        // Checkout buttons
        checkoutButtons: [
            'button[name="checkout"]',
            '[data-checkout-button]',
            '.cart__checkout-button',
            'input[name="checkout"]',
            'a[href="/checkout"]'
        ]
    };

    // SH Update: State
    var state = {
        selectedType: null,
        isModalOpen: false,
        isCartPage: false,
        pendingCheckout: null
    };

    /**
     * Check if we're on the cart page
     */
    function isCartPage() {
        // return window.location.pathname === '/cart' ||
        //     window.location.pathname.startsWith('/cart');
        var modal = getModal();
        var type = modal && modal.getAttribute('data-page-type');

        if (type) {
            return type === 'cart';
        }

        return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart(?:\/|$)/i.test(window.location.pathname);
    }

    /**
     * SH Update: Generate a unique session ID
     */
    function generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * SH Update: Initialize session and clear data if new session detected
     * This ensures all shopper data resets when browser/tab is closed
     */
    function initSession() {
        var sessionSessionId = sessionStorage.getItem(STORAGE_KEYS.sessionId);
        var localSessionId = localStorage.getItem(STORAGE_KEYS.sessionId);

        // If sessionStorage has no ID, this is a new session (new tab or browser reopened)
        if (!sessionSessionId) {
            // Clear all shopper data from localStorage
            localStorage.removeItem(STORAGE_KEYS.sessionId);
            localStorage.removeItem(STORAGE_KEYS.type);
            localStorage.removeItem(STORAGE_KEYS.email);
            localStorage.removeItem(STORAGE_KEYS.companyEmail);
            localStorage.removeItem(STORAGE_KEYS.timestamp);

            // Generate new session ID and store in both
            var newSessionId = generateSessionId();
            sessionStorage.setItem(STORAGE_KEYS.sessionId, newSessionId);
            localStorage.setItem(STORAGE_KEYS.sessionId, newSessionId);
        }
    }

    /**
     * SH Update: Check for pending redirect after native form submission
     * If user clicked "Register as Company" and form fell back to native submit,
     * we need to redirect them after the page reloads.
     * @returns {boolean} true if redirect was performed, false otherwise
     */
    function checkPendingRedirect() {
        var pendingRedirect = sessionStorage.getItem(STORAGE_KEYS.pendingRedirect);
        if (pendingRedirect) {
            // Clear the pending redirect immediately to prevent loops
            sessionStorage.removeItem(STORAGE_KEYS.pendingRedirect);
            // Perform the redirect
            window.location.href = pendingRedirect;
            return true;
        }
        return false;
    }

    /**
     * SH Update: Check for pending success state after native form submission.
     * If personal email was submitted via native form (page reloaded), show coupon.
     * @returns {boolean} true if pending success was found
     */
    function checkPendingSuccess() {
        var pendingSuccess = sessionStorage.getItem(STORAGE_KEYS.pendingSuccess);
        if (pendingSuccess === 'personal') {
            sessionStorage.removeItem(STORAGE_KEYS.pendingSuccess);
            return true;
        }
        return false;
    }

    /**
     * Check if form was already answered this session
     * Uses sessionStorage so popup reappears on new sessions (tab close/reopen)
     */
    function isAlreadyAnswered() {
        return sessionStorage.getItem(STORAGE_KEYS.answered) === 'true';
    }

    /**
     * SH Update: Check if modal was dismissed without completing
     * Uses sessionStorage so it resets on new sessions
     */
    function isModalDismissed() {
        return sessionStorage.getItem(STORAGE_KEYS.dismissed) === 'true';
    }

    /**
     * SH Update: Mark modal as dismissed (user closed without filling)
     * This prevents modal from showing again, but cart form is still required
     */
    function setModalDismissed() {
        sessionStorage.setItem(STORAGE_KEYS.dismissed, 'true');
    }

    /**
     * Save selection to storage
     * - 'answered' flag goes to sessionStorage (per-session)
     * - Other data goes to localStorage (persistent)
     */
    function saveSelection(type, email) {
        // Per-session flag (clears when tab closes)
        sessionStorage.setItem(STORAGE_KEYS.answered, 'true');
        // Persistent data
        localStorage.setItem(STORAGE_KEYS.type, type);
        localStorage.setItem(STORAGE_KEYS.timestamp, new Date().toISOString());
        if (email) {
            localStorage.setItem(STORAGE_KEYS.email, email);
        }
    }

    /**
     * Get modal element
     */
    function getModal() {
        return document.querySelector(SELECTORS.modal);
    }

    /**
     * Get cart form element
     */
    function getCartForm() {
        return document.querySelector(SELECTORS.cartForm);
    }

    /**
     * Show modal
     * @param {string} showSection - 'personal', 'company', or null (initial state with dropdown only)
     */
    function showModal(showSection) {
        var modal = getModal();
        if (!modal) return;

        var personalSection = modal.querySelector(SELECTORS.personalSection);
        var personalSuccess = modal.querySelector(SELECTORS.personalSuccess);
        var companySection = modal.querySelector(SELECTORS.companySection);
        var companyButtons = modal.querySelector(SELECTORS.companyButtons);
        var modalSelect = modal.querySelector(SELECTORS.modalSelect);

        // Reset all sections to hidden
        if (personalSection) personalSection.classList.add('sh-hidden');
        if (personalSuccess) personalSuccess.classList.add('sh-hidden');
        if (companySection) companySection.classList.add('sh-hidden');
        if (companyButtons) companyButtons.classList.add('sh-hidden');

        // Show specific section if requested
        if (showSection === 'personal') {
            if (personalSection) personalSection.classList.remove('sh-hidden');
            // Pre-select dropdown
            if (modalSelect) modalSelect.value = 'personal';
            state.selectedType = 'personal';
        } else if (showSection === 'company') {
            if (companySection) companySection.classList.remove('sh-hidden');
            // Pre-select dropdown
            if (modalSelect) modalSelect.value = 'company';
            state.selectedType = 'company';
        }

        modal.classList.remove('sh-hidden');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        state.isModalOpen = true;

        // Focus appropriate element
        var focusTarget;
        if (showSection === 'personal') {
            focusTarget = modal.querySelector(SELECTORS.emailInput);
        } else if (showSection === 'company') {
            focusTarget = modal.querySelector(SELECTORS.companyEmail);
        } else {
            focusTarget = modal.querySelector(SELECTORS.modalSelect);
        }

        if (focusTarget) {
            setTimeout(function() { focusTarget.focus(); }, 100);
        }
    }

    /**
     * Hide modal
     */
    function hideModal() {
        var modal = getModal();
        if (!modal) return;

        modal.classList.add('sh-hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        state.isModalOpen = false;

        // Reset modal state
        resetModal(modal);
    }

    /**
     * Reset modal to initial state
     */
    function resetModal(modal) {
        var modalSelect = modal.querySelector(SELECTORS.modalSelect);
        var emailInput = modal.querySelector(SELECTORS.emailInput);
        var companyEmailInput = modal.querySelector(SELECTORS.companyEmail);
        var personalSection = modal.querySelector(SELECTORS.personalSection);
        var personalSuccess = modal.querySelector(SELECTORS.personalSuccess);
        var companySection = modal.querySelector(SELECTORS.companySection);
        var companyButtons = modal.querySelector(SELECTORS.companyButtons);

        // Hide all dynamic sections
        if (personalSection) personalSection.classList.add('sh-hidden');
        if (personalSuccess) personalSuccess.classList.add('sh-hidden');
        if (companySection) companySection.classList.add('sh-hidden');
        if (companyButtons) companyButtons.classList.add('sh-hidden');

        // Clear form values
        if (modalSelect) modalSelect.value = '';
        if (emailInput) emailInput.value = '';
        if (companyEmailInput) companyEmailInput.value = '';

        hideAllErrors(modal);
        state.selectedType = null;
    }

    /**
     * SH Update: Show Personal email section
     */
    function showPersonalSection(modal) {
        var personalSection = modal.querySelector(SELECTORS.personalSection);
        var personalSuccess = modal.querySelector(SELECTORS.personalSuccess);
        var companySection = modal.querySelector(SELECTORS.companySection);
        var emailInput = modal.querySelector(SELECTORS.emailInput);

        // Hide company section and success section if showing
        if (companySection) companySection.classList.add('sh-hidden');
        if (personalSuccess) personalSuccess.classList.add('sh-hidden');

        // Show personal section
        if (personalSection) personalSection.classList.remove('sh-hidden');

        if (emailInput) {
            setTimeout(function() { emailInput.focus(); }, 100);
        }
    }

    /**
     * SH Update: Show Company email section
     */
    function showCompanySection(modal) {
        var personalSection = modal.querySelector(SELECTORS.personalSection);
        var companySection = modal.querySelector(SELECTORS.companySection);
        var companyButtons = modal.querySelector(SELECTORS.companyButtons);
        var companyEmailInput = modal.querySelector(SELECTORS.companyEmail);

        // Hide personal section if showing
        if (personalSection) personalSection.classList.add('sh-hidden');

        // Show company section
        if (companySection) companySection.classList.remove('sh-hidden');

        // Check if email field already has a valid email (e.g., switching back from personal)
        var existingEmail = companyEmailInput ? companyEmailInput.value.trim() : '';
        if (existingEmail && isValidEmail(existingEmail)) {
            // Show buttons if valid email already exists
            if (companyButtons) companyButtons.classList.remove('sh-hidden');
        } else {
            // Hide buttons initially (shown when valid email entered)
            if (companyButtons) companyButtons.classList.add('sh-hidden');
        }

        if (companyEmailInput) {
            setTimeout(function() { companyEmailInput.focus(); }, 100);
        }
    }

    /**
     * SH Update: Show Personal success section with coupon code
     */
    function showPersonalSuccess(modal) {
        var personalSection = modal.querySelector(SELECTORS.personalSection);
        var personalSuccess = modal.querySelector(SELECTORS.personalSuccess);
        var companySection = modal.querySelector(SELECTORS.companySection);
        var questionField = modal.querySelector(SELECTORS.questionField);

        // Hide all other sections
        if (personalSection) personalSection.classList.add('sh-hidden');
        if (companySection) companySection.classList.add('sh-hidden');
        if (questionField) questionField.classList.add('sh-hidden');

        // Show success section
        if (personalSuccess) personalSuccess.classList.remove('sh-hidden');
    }

    /**
     * SH Update: Handle company email input (real-time validation to show/hide buttons)
     */
    function handleCompanyEmailInput(event) {
        var modal = getModal();
        if (!modal) return;

        var companyButtons = modal.querySelector(SELECTORS.companyButtons);
        var companyEmailError = modal.querySelector(SELECTORS.companyEmailError);
        var email = event.target.value.trim();

        hideError(companyEmailError);

        if (email && isValidEmail(email)) {
            // Show the dual buttons when email is valid
            if (companyButtons) companyButtons.classList.remove('sh-hidden');
        } else {
            // Hide the dual buttons when email is invalid
            if (companyButtons) companyButtons.classList.add('sh-hidden');
        }
    }

    /**
     * SH Update: Handle company button click (Register or Continue Shopping)
     * Submits to Shopify customer form via AJAX with native fallback
     * @param {string} action - 'register' or 'continue'
     */
    function handleCompanyButtonClick(action) {
        var modal = getModal();
        if (!modal) return;

        var companyForm = modal.querySelector(SELECTORS.companyForm);
        var companyEmailInput = modal.querySelector(SELECTORS.companyEmail);
        var companyEmailError = modal.querySelector(SELECTORS.companyEmailError);
        var registerBtn = modal.querySelector(SELECTORS.registerBtn);
        var continueBtn = modal.querySelector(SELECTORS.continueBtn);
        var email = companyEmailInput ? companyEmailInput.value.trim() : '';

        hideError(companyEmailError);

        if (!email || !isValidEmail(email)) {
            showError(companyEmailError);
            if (companyEmailInput) companyEmailInput.focus();
            return;
        }

        var redirectUrl = COMPANY_REDIRECT_URL;

        if (registerBtn && registerBtn.getAttribute('data-redirect')) {
            redirectUrl = registerBtn.getAttribute('data-redirect');
        } else if (modal.getAttribute('data-company-url')) {
            redirectUrl = modal.getAttribute('data-company-url');
        }

        if (registerBtn) registerBtn.disabled = true;
        if (continueBtn) continueBtn.disabled = true;

        var pendingAction = action;
        var formData = new FormData(companyForm);

        localStorage.setItem(STORAGE_KEYS.companyEmail, email);
        localStorage.setItem(STORAGE_KEYS.type, 'company');
        localStorage.setItem(STORAGE_KEYS.timestamp, new Date().toISOString());
        sessionStorage.setItem(STORAGE_KEYS.answered, 'true');

        fetch(companyForm.action, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then(function(response) {
                if (response.ok) {
                    if (registerBtn) registerBtn.disabled = false;
                    if (continueBtn) continueBtn.disabled = false;

                    hideCartForm();
                } else {
                    console.warn('AJAX submission returned ' + response.status + '. Falling back to native submission.');

                    if (pendingAction === 'register' && redirectUrl) {
                        sessionStorage.setItem(STORAGE_KEYS.pendingRedirect, redirectUrl);
                    }

                    companyForm.submit();
                }
            })
            .catch(function(error) {
                console.error('Submission network error:', error);

                if (pendingAction === 'register' && redirectUrl) {
                    sessionStorage.setItem(STORAGE_KEYS.pendingRedirect, redirectUrl);
                }

                companyForm.submit();
            });
    }

    /**
     * Show error message
     */
    function showError(errorEl) {
        if (errorEl) {
            errorEl.classList.remove('sh-hidden');
        }
    }

    /**
     * Hide error message
     */
    function hideError(errorEl) {
        if (errorEl) {
            errorEl.classList.add('sh-hidden');
        }
    }

    /**
     * Hide all error messages in a container
     */
    function hideAllErrors(container) {
        var errors = container.querySelectorAll('[class*="-error"]');
        errors.forEach(function(err) {
            if (err.classList.contains('js-sh-shopper-error') ||
                err.classList.contains('js-sh-shopper-step1-error') ||
                err.classList.contains('js-sh-shopper-email-error')) {
                err.classList.add('sh-hidden');
            }
        });
    }

    /**
     * Validate email format
     */
    function isValidEmail(email) {
        var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Hide cart form after selection
     */
    function hideCartForm() {
        var cartForm = getCartForm();
        if (cartForm) {
            cartForm.style.display = 'none';
        }
    }

    /**
     * Enable checkout buttons
     */
    function enableCheckout() {
        var buttons = [];
        SELECTORS.checkoutButtons.forEach(function(selector) {
            document.querySelectorAll(selector).forEach(function(btn) {
                if (buttons.indexOf(btn) === -1) {
                    buttons.push(btn);
                }
            });
        });

        buttons.forEach(function(btn) {
            btn.disabled = false;
            btn.style.opacity = '1';
            btn.style.cursor = 'pointer';
            btn.removeAttribute('title');
        });
    }

    /**
     * Disable checkout buttons
     */
    function disableCheckout() {
        var buttons = [];
        SELECTORS.checkoutButtons.forEach(function(selector) {
            document.querySelectorAll(selector).forEach(function(btn) {
                if (buttons.indexOf(btn) === -1) {
                    buttons.push(btn);
                }
            });
        });

        buttons.forEach(function(btn) {
            btn.disabled = true;
            btn.style.opacity = '0.5';
            btn.style.cursor = 'not-allowed';
            btn.setAttribute('title', 'Please select who you are shopping for');
        });
    }

    /**
     * Proceed to checkout
     */
    function proceedToCheckout() {
        hideModal();

        if (state.pendingCheckout) {
            var form = state.pendingCheckout.closest('form');
            if (form) {
                form.submit();
            } else if (state.pendingCheckout.tagName === 'A') {
                window.location.href = state.pendingCheckout.href;
            } else {
                window.location.href = '/checkout';
            }
            state.pendingCheckout = null;
        } else {
            window.location.href = '/checkout';
        }
    }

    /**
     * Handle cart page inline form selection
     */
    function handleCartFormSelection(event) {
        var value = event.target.value;
        var cartForm = getCartForm();
        var loginUrl = cartForm ? cartForm.getAttribute('data-login-url') : '/account/login';

        if (cartForm) hideAllErrors(cartForm);
        state.selectedType = value;

        if (value === 'personal') {
            // SH Update: Personal - open modal with personal section visible
            showModal('personal');

        } else if (value === 'company') {
            // SH Update: Company - open modal with company section visible (NO redirect)
            showModal('company');

        } else if (value === 'account') {
            // SH Update: Account - redirect to login
            var checkoutUrl = encodeURIComponent('/checkout');
            window.location.href = loginUrl + '?checkout_url=' + checkoutUrl;

        } else {
            // SH Update: Any other value (school, nonprofit, etc.) - save and enable checkout
            saveSelection(value);
            hideCartForm();
            enableCheckout();
        }
    }

    /**
     * Handle modal dropdown selection
     */
    function handleModalSelection(event) {
        var modal = getModal();
        if (!modal) return;

        var value = event.target.value;
        var loginUrl = modal.getAttribute('data-login-url') || '/account/login';

        hideAllErrors(modal);
        state.selectedType = value;

        if (value === 'personal') {
            showPersonalSection(modal);
        } else if (value === 'company') {
            showCompanySection(modal);
        } else if (value === 'account') {
            saveSelection(value);
            hideModal();
            window.location.href = loginUrl;
        } else {
            saveSelection(value);
            hideModal();
        }
    }

    /**
     * Handle email submission (Step 2)
     * Submits to Shopify customer form for newsletter subscription + 30% discount
     * Now used for Personal shoppers only (Company redirects to registration page)
     */
    function handleEmailSubmit(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        var modal = getModal();
        if (!modal) return;

        var form = modal.querySelector(SELECTORS.emailForm);
        var emailInput = modal.querySelector(SELECTORS.emailInput);
        var emailError = modal.querySelector(SELECTORS.emailError);
        var submitBtn = modal.querySelector(SELECTORS.submitBtn);
        var email = emailInput ? emailInput.value.trim() : '';

        hideError(emailError);

        if (!email || !isValidEmail(email)) {
            showError(emailError);
            if (emailInput) emailInput.focus();
            return;
        }

        // SH Update: Disable submit button during submission
        var originalButtonText = '';
        if (submitBtn) {
            originalButtonText = submitBtn.textContent;
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';
        }

        // Prepare form data
        var formData = new FormData(form);

        // Submit to Shopify
        fetch(form.action, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then(function(response) {
                // If successful (2xx status), handle via AJAX
                if (response.ok) {

                    // Save selection locally
                    saveSelection('personal', email);

                    // SH Update: Re-enable button
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = originalButtonText || 'Get My Discount';
                    }

                    // SH Update: Show success section with coupon code instead of closing
                    if (state.isCartPage) {
                        // On cart page, show success briefly then proceed to checkout
                        showPersonalSuccess(modal);
                        setTimeout(function() {
                            hideCartForm();
                            enableCheckout();
                            proceedToCheckout();
                        }, 3000);
                    } else {
                        // On other pages, show success section (user closes manually)
                        showPersonalSuccess(modal);
                    }
                } else {
                    // If error (e.g. 400 CAPTCHA challenge), fall back to native submission
                    console.warn('AJAX submission returned ' + response.status + '. Falling back to native submission.');
                    saveSelection('personal', email);
                    // SH Update: Save pending success state before native form submit
                    sessionStorage.setItem(STORAGE_KEYS.pendingSuccess, 'personal');
                    form.submit();
                }
            })
            .catch(function(error) {
                console.error('Submission network error:', error);
                // Fallback to native submission
                saveSelection('personal', email);
                // SH Update: Save pending success state before native form submit
                sessionStorage.setItem(STORAGE_KEYS.pendingSuccess, 'personal');
                form.submit();
            });
    }

    /**
     * Handle checkout button click (cart page only)
     */
    function handleCheckoutClick(event) {
        if (!state.isCartPage) return;

        // If already answered, allow checkout
        if (isAlreadyAnswered()) return;

        // Prevent default checkout
        event.preventDefault();
        event.stopPropagation();

        // Store the clicked button for later
        state.pendingCheckout = event.currentTarget;

        // Show error and scroll to form
        var cartForm = getCartForm();
        if (cartForm) {
            var errorEl = cartForm.querySelector(SELECTORS.cartError);
            showError(errorEl);
            cartForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    /**
     * Handle keyboard events
     */
    function handleKeydown(event) {
        if (!state.isModalOpen) return;

        // Escape key closes modal
        if (event.key === 'Escape') {
            // If on cart page and a selection was made, reset the cart dropdown
            if (state.isCartPage && (state.selectedType === 'personal' || state.selectedType === 'company')) {
                var cartForm = getCartForm();
                if (cartForm) {
                    var cartSelect = cartForm.querySelector(SELECTORS.cartSelect);
                    if (cartSelect) cartSelect.value = '';
                }
            }
            // SH Update: Mark modal as dismissed if closed without completing (non-cart pages only)
            if (!state.isCartPage) {
                setModalDismissed();
            }
            hideModal();
        }
    }

    /**
     * Initialize cart page functionality
     */
    function initCartPage() {
        var cartForm = getCartForm();
        if (!cartForm) return;

        // SH Update: If already answered, hide form and enable checkout
        if (isAlreadyAnswered()) {
            cartForm.style.display = 'none';
            enableCheckout();
            return;
        }

        // SH Update: Disable checkout until answered
        disableCheckout();

        // Listen to cart form dropdown
        var cartSelect = cartForm.querySelector(SELECTORS.cartSelect);
        if (cartSelect) {
            cartSelect.addEventListener('change', handleCartFormSelection);
        }

        // Intercept checkout button clicks
        SELECTORS.checkoutButtons.forEach(function(selector) {
            document.querySelectorAll(selector).forEach(function(btn) {
                btn.addEventListener('click', handleCheckoutClick, true);
            });
        });
    }

    /**
     * Initialize modal functionality
     */
    function initModal() {
        var modal = getModal();
        if (!modal) return;

        // Modal event listeners
        var overlay = modal.querySelector(SELECTORS.modalOverlay);
        var closeBtn = modal.querySelector(SELECTORS.modalClose);
        var modalSelect = modal.querySelector(SELECTORS.modalSelect);
        var submitBtn = modal.querySelector(SELECTORS.submitBtn);
        var emailInput = modal.querySelector(SELECTORS.emailInput);

        if (overlay) {
            overlay.addEventListener('click', function() {
                // If on cart page and a selection was made, reset the cart dropdown
                if (state.isCartPage && (state.selectedType === 'personal' || state.selectedType === 'company')) {
                    var cartForm = getCartForm();
                    if (cartForm) {
                        var cartSelect = cartForm.querySelector(SELECTORS.cartSelect);
                        if (cartSelect) cartSelect.value = '';
                    }
                }
                // SH Update: Mark modal as dismissed if closed without completing (non-cart pages only)
                if (!state.isCartPage) {
                    setModalDismissed();
                }
                hideModal();
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', function() {
                // If on cart page and a selection was made, reset the cart dropdown
                if (state.isCartPage && (state.selectedType === 'personal' || state.selectedType === 'company')) {
                    var cartForm = getCartForm();
                    if (cartForm) {
                        var cartSelect = cartForm.querySelector(SELECTORS.cartSelect);
                        if (cartSelect) cartSelect.value = '';
                    }
                }
                // SH Update: Mark modal as dismissed if closed without completing (non-cart pages only)
                if (!state.isCartPage) {
                    setModalDismissed();
                }
                hideModal();
            });
        }

        // SH Update: Close success button (after showing coupon code)
        var closeSuccessBtn = modal.querySelector(SELECTORS.closeSuccessBtn);
        if (closeSuccessBtn) {
            closeSuccessBtn.addEventListener('click', function() {
                hideModal();
            });
        }

        if (modalSelect) {
            modalSelect.addEventListener('change', handleModalSelection);
        }

        // SH Update: Personal form - attach to form submit event for newsletter subscription
        var emailForm = modal.querySelector(SELECTORS.emailForm);

        if (emailForm) {
            emailForm.addEventListener('submit', handleEmailSubmit, true); // Use capture phase
        }

        // Always attach click listener to button as primary handler
        if (submitBtn) {
            submitBtn.type = 'button'; // Change from submit to button to prevent form submission
            submitBtn.addEventListener('click', function(e) {
                handleEmailSubmit(e);
            });
        }

        // SH Update: Company email real-time validation
        var companyEmailInput = modal.querySelector(SELECTORS.companyEmail);
        if (companyEmailInput) {
            companyEmailInput.addEventListener('input', handleCompanyEmailInput);
            // Also handle on paste
            companyEmailInput.addEventListener('paste', function(e) {
                setTimeout(function() { handleCompanyEmailInput(e); }, 10);
            });
        }

        // SH Update: Company Register button click
        var registerBtn = modal.querySelector(SELECTORS.registerBtn);
        if (registerBtn) {
            registerBtn.addEventListener('click', function(e) {
                e.preventDefault();
                handleCompanyButtonClick('register');
            });
        }

        // SH Update: Company Continue Shopping button click
        var continueBtn = modal.querySelector(SELECTORS.continueBtn);
        if (continueBtn) {
            continueBtn.addEventListener('click', function(e) {
                e.preventDefault();
                handleCompanyButtonClick('continue');
            });
        }

        // Keyboard events
        document.addEventListener('keydown', handleKeydown);
    }

    function isMobileViewport() {
        return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
    }

    /**
     * Initialize site entry popup (non-cart pages)
     */
    function initSiteEntry() {
        // SH Update: Only show popup on non-cart pages if not already answered
        if (state.isCartPage) return;
        if (isMobileViewport()) return;
        if (isAlreadyAnswered()) return;
        // SH Update: Don't show if user previously dismissed the modal
        // They will still need to fill the form at cart level to checkout
        if (isModalDismissed()) return;

        var modal = getModal();
        if (!modal) return;

        // Show modal on site entry (dropdown only, no email section)
        showModal(null);
    }

    /**
     * Initialize
     */
    function init() {
        // SH Update: Initialize session first - clears data if new session detected
        initSession();

        // SH Update: Check for pending redirect (e.g., after native form submit for company registration)
        // If redirect is found, exit early - the page will navigate away
        if (checkPendingRedirect()) {
            return;
        }

        state.isCartPage = isCartPage();

        // Initialize cart page functionality
        if (state.isCartPage) {
            initCartPage();
        }

        // Initialize modal (available on all pages)
        initModal();

        // SH Update: Check for pending success state (after native form submit for personal)
        // If found, show modal with success section (coupon code)
        if (checkPendingSuccess()) {
            var modal = getModal();
            if (modal) {
                modal.classList.remove('sh-hidden');
                modal.setAttribute('aria-hidden', 'false');
                document.body.style.overflow = 'hidden';
                state.isModalOpen = true;
                showPersonalSuccess(modal);
            }
            return; // Skip normal site entry flow
        }

        // SH Update: Show popup on site entry for non-cart pages
        initSiteEntry();
    }

    // SH Update: Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();